// Type-only: `import type` emits no module edge, so clp-viewer3d — and the ~815KB of
// three.js behind it — stays out of the entry chunk. The *values* are pulled in by the
// dynamic `import()` kicked off in the constructor and awaited in `init()`; adding a plain
// `import { … }` here would put the whole 3D stack back on the critical path.
import type {
  API,
  BoundsDebugPlugin,
  BuilderPlatformInfo,
  PlatformBuilder,
  ProductPartViewNode,
  SceneSpecificationBuilder,
  View3dConfigurationDto,
} from '@canvas-logic/clp-viewer3d';

import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { NotificationService } from '../services/NotificationService';
import Localization, { Locale } from '../services/Localization';
import { CompositeApiSdk } from '@/types';
import { createConfiguratorContainerUIElementState, createConfiguratorUIElementState } from '@/elements/elementFactory';
import { ChangeComponentAttributesVisibilityEvent, UpdateDependentAttributesEvent } from '@/elements/base/events';
import { ElementState, ConfiguratorUIElementStates, ContainerStateBase } from '@/elements/base/ElementState';
import { IPrice, PartsRegistry, ralToHex } from '@canvas-logic/clp-domain';
import {
  IConfiguratorCatalogProductApiOptions,
  IConfiguratorUIDto,
  IUiCfgContainer,
  IUiCfgContainerItem,
  PresetValidationResult,
  ILocalizationConfigDto,
  DEFAULT_LOCALIZATION_CONFIG,
} from '@canvas-logic/clp-api-types';
import { AppConfig } from '@/config';
import { resolveEffectiveLocale, resolveEffectiveRegion } from '../services/localization-config/resolveEffectiveRegion';
import { getUrlLocale } from '../services/localization-config/getUrlLocale';
import i18next from '../../i18n';
import { getUrlCurrency } from '../services/localization-config/getUrlCurrency';
import { PdfService } from '@/ui-kit/services/PdfService';
import type { TranslationLayers } from '@canvas-logic/clp-localization';
import { OptionsConfiguratorUIStore } from '@/stores/OptionsConfiguratorUIStore';
import { LookupStorage } from '@/ui-kit/domain/LookupStorage';
import { buildSlotRenderSpecs, FetcherApi, HqrImageResolver } from '@canvas-logic/clp-sdk';
import { IHqrConfiguratorDto } from '@canvas-logic/clp-domain';
import { ContactUsService } from '@/ui-kit/services/ContactUsService';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { addSearchParamToHostUrl, addSearchParamToUrl } from '@/helpers/url';
import { ConfiguratorParams } from '@/constants';
import { AttributeStateBase } from '@/elements/base/AttributeStateBase';
import { ComponentElementState } from '@/elements/ComponentElement/ComponentElementState';
import { prepareUILayout } from '@/ui-kit/domain/virtualLayoutBuilder';

const SKIP_RENDERING = false;

const traverseChildren = (item: ConfiguratorUIElementStates, fn: (it: ConfiguratorUIElementStates) => void) => {
  fn(item);
  if ('children' in item) {
    for (const child of item.children) {
      traverseChildren(child, fn);
    }
  }
};

export interface Basic3dLayoutParams {
  configuratorUI: IConfiguratorUIDto;
  view3d: View3dConfigurationDto;
  sdk: CompositeApiSdk;
  projectId: number;
  productId: number;
  draft: boolean;
  params: IConfiguratorCatalogProductApiOptions;
  fetcher: FetcherApi;
  without3d?: boolean;
}

export class Bootstrapper {
  // Kicked off (not awaited) by the constructor when `params.without3d` is unset — the same
  // condition `skipRendering` gates its single use (`init()`) on. Starting the fetch here
  // overlaps the chunk download with the rest of the bootstrap, so deferring the module does
  // not push out the moment the scene is ready.
  #viewer3d?: Promise<typeof import('./viewer3dBindings')>;
  public api!: API;
  // Debug-only (?debug): draws SDK-computed bounding boxes in the viewer.
  private boundsDebugPlugin?: BoundsDebugPlugin;
  private pdfService!: PdfService;
  private sceneSpecificationBuilder!: SceneSpecificationBuilder;
  #lookupStorage?: LookupStorage;
  // True once init()'s own first scene build has completed. Until then the 3D sync in
  // handleProductChanges() must not run: `this.api` is assigned well before the scene
  // exists, so syncing on "api is set" would race init()'s own showSceneV2().
  #sceneReady = false;
  // A product change arrived before #sceneReady; replay the 3D sync once init() is done.
  #pendingSceneSync = false;
  contactUsService: ContactUsService | null = null;
  showPrice: boolean = false;
  showRuler = false;
  view3dError: Error | null = null;
  presetValidation: PresetValidationResult | undefined;
  hqrConfig: IHqrConfiguratorDto | null = null;
  // The next three are populated by `buildLayout()`, which the constructor calls.
  rootElement!: ContainerStateBase;

  optionsViewStore: OptionsConfiguratorUIStore;

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  productVariant!: {};
  configuratorUI: IConfiguratorUIDto;
  view3dConfigurationConfig: View3dConfigurationDto;

  localization: Localization;
  notificationService: NotificationService;
  layoutRoot!: IUiCfgContainer;

  readonly sdk: CompositeApiSdk;
  totalPrice: IPrice = {};
  selectedCurrency: string | undefined = getUrlCurrency(window.location, embeddingService.initialLocation);
  effectiveLocale: string = DEFAULT_LOCALIZATION_CONFIG.defaultRegion;
  effectiveRegion: string = DEFAULT_LOCALIZATION_CONFIG.defaultRegion;
  private readonly productId: number;
  public readonly projectId: number;
  private readonly draft: boolean;
  private readonly isCatalog: boolean = false;
  private elements: ElementState[] = [];
  private builder!: PlatformBuilder;
  private context!: BuilderPlatformInfo;

  get catalogId(): number {
    if (!this.params.params.catalogId) {
      throw new Error('Not supported');
    }
    return this.params.params.catalogId;
  }

  constructor(private readonly canvas: HTMLCanvasElement, private readonly params: Basic3dLayoutParams) {
    this.isCatalog = !!params.params.catalogId;

    if (params.params.catalogId) {
      this.contactUsService = new ContactUsService(params.params, params.fetcher);

      params.fetcher.getPricingFeatureSchema(params.params).then((schema) => {
        this.showPrice = schema.enabled;
      });
    }
    this.optionsViewStore = new OptionsConfiguratorUIStore();
    if (!params.without3d) {
      // Fire-and-forget: the constructor must stay synchronous (BootstrapperContext hands
      // this instance to the side panel before awaiting init()). `View3dFactory`'s ctor is
      // empty, so building it in init() instead of here changes nothing observable.
      //
      // Import the narrow re-export facade, never '@canvas-logic/clp-viewer3d' directly:
      // Vite's `__vitePreload` rewrite makes Rollup keep every export of a dynamically
      // imported module alive, which drags in 2.06MB of rapier wasm. See viewer3dBindings.ts.
      const viewer3d = import('./viewer3dBindings');
      // A chunk that never loads is handled by init()'s catch (→ `view3dError`); this only
      // keeps the rejection from being reported as unhandled if init() is never called.
      void viewer3d.catch(() => undefined);
      this.#viewer3d = viewer3d;
    }

    this.sdk = params.sdk;
    this.configuratorUI = params.configuratorUI;
    this.productId = params.productId;
    this.projectId = params.projectId;
    this.draft = params.draft;
    if (!this.sdk.composite) {
      throw new Error('Not supported');
    }
    this.buildLayout();

    this.view3dConfigurationConfig = params.view3d;
    this.localization = new Localization(params.configuratorUI.localization, Locale.en);
    this.notificationService = new NotificationService();
    // ADR-0066 degrade-on-read: a link-opened configuration whose references
    // no longer resolved exactly was healed in memory by the server — tell
    // the user the configuration may have changed. The stored link itself is
    // never rewritten.
    if (this.sdk.composite && this.sdk.api.isConfigurationMigrated()) {
      void this.notificationService.info(i18next.t('configuration_changed_notice'));
    }
    if (this.sdk.composite) {
      this.#lookupStorage = new LookupStorage(this.sdk.api);
    }

    this.presetValidation = this.sdk.api.getPresetValidation();

    this.subscribeToAutoCorrection();
    this.updateAllAttributes();
    makeObservable(this, {
      productVariant: observable,
      totalPrice: observable,
      selectedCurrency: observable,
      effectiveLocale: observable,
      effectiveRegion: observable,
      showRuler: observable,
      view3dError: observable,
      showPrice: observable,
      presetValidation: observable,
      hqrConfig: observable,
      hqrImageUrl: computed,
      rootElement: observable.ref,
      handleProductChanges: action.bound,
      addToCart: action.bound,
      switchRuler: action.bound,
      createLink: action.bound,
      buildLayout: action.bound,
      configuratorUI: observable,
      updateLayoutConfigSettings: action.bound,
    });

    // Load HQR config once on init
    this.loadHqrConfig();
    this.updateTotalPrice();
    embeddingService.subscribeOnLayoutConfigChange((layoutConfig) => {
      this.updateLayoutConfigSettings({ ...layoutConfig.payload });
    }, []);

    embeddingService.subscribeOnScrollToElement((message) => {
      this.scrollToElement(message.payload.elementId, message.payload.fallbackIds);
    }, []);
  }

  buildLayout() {
    this.elements = [];
    this.productVariant = this.sdk.api.getVariant(); // params.configuration;
    this.layoutRoot = prepareUILayout(this.configuratorUI.layout.root);
    const traverseChildren = (item: IUiCfgContainerItem, parent: IUiCfgContainer, _prevItem?: IUiCfgContainerItem) => {
      if (item.kind === 'container') {
        const children: ElementState[] = [];
        let prev: IUiCfgContainerItem | undefined;
        for (const child of item.children) {
          children.push(traverseChildren(child, item, prev));
          prev = child;
        }

        const elementState = createConfiguratorContainerUIElementState(this.sdk.api, item, children);
        this.elements.push(elementState);

        return elementState;
      } else {
        const elementState = createConfiguratorUIElementState(this.sdk.api, item, parent);

        elementState.addEventListener((payload) => {
          if (payload.event === 'changeComponentAttributesVisibility') {
            this.handleComponentAttributesVisibilityChange(payload);
          }
          if (payload.event === 'updateDependentAttributes') {
            this.handleUpdateDependentAttributes(payload);
          }
        });
        this.elements.push(elementState);
        return elementState;
      }
    };

    // The root is itself a container, so it takes the `kind === 'container'` branch and
    // never reads `parent`; passing itself satisfies the required parameter without
    // changing what runs.
    this.rootElement = traverseChildren(this.layoutRoot, this.layoutRoot) as ContainerStateBase;
  }

  updateLayoutConfigSettings(layout: IConfiguratorUIDto) {
    this.configuratorUI = layout;
    this.buildLayout();
  }

  private scrollToElement(elementId: string, fallbackIds?: string[]) {
    const idsToTry = [elementId, ...(fallbackIds ?? [])];
    let element: Element | null = null;

    for (const id of idsToTry) {
      element = document.querySelector(`[data-element-id="${CSS.escape(id)}"]`);
      if (element) break;
    }

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const htmlElement = element as HTMLElement;
    htmlElement.classList.remove('cp-highlight-pulse');
    // Force reflow so re-adding the class restarts the animation
    void htmlElement.offsetWidth;
    htmlElement.classList.add('cp-highlight-pulse');
    htmlElement.addEventListener(
      'animationend',
      () => {
        htmlElement.classList.remove('cp-highlight-pulse');
      },
      { once: true },
    );
  }

  get lookupStorage() {
    if (!this.#lookupStorage) {
      throw new Error('Lookup storage is not initialized');
    }
    return this.#lookupStorage;
  }

  private handleComponentAttributesVisibilityChange(payload: ChangeComponentAttributesVisibilityEvent) {
    traverseChildren(this.rootElement, (element) => {
      if (
        element instanceof AttributeStateBase &&
        element.componentKey === payload.componentKey &&
        element.id !== payload.target.id
      ) {
        element.setVisibility(payload.visible);
      }
      if (element instanceof ComponentElementState && element.componentKey === payload.componentKey) {
        element.productLookup.updateValues();
      }
    });
    this.handleProductChanges();
  }

  get isComposite(): boolean {
    return this.sdk.composite;
  }

  get isInCatalog(): boolean {
    return this.sdk.composite && this.sdk.api.isInCatalog;
  }

  get hasPrice(): boolean {
    return this.showPrice && !!Object.keys(this.totalPrice).length;
  }

  /**
   * Computed property that returns the HQR image URL for the current configuration.
   * Returns null if HQR is not configured or no image matches the current variant.
   * Depends on productVariant observable so it recalculates when variant changes.
   */
  get hqrImageUrl(): string | null {
    if (!this.hqrConfig || !this.hqrConfig.enabled || !this.sdk.composite) {
      return null;
    }

    // Use this.productVariant (observable) so MobX tracks the dependency
    // and recalculates when variant changes
    const currentVariant = this.productVariant;
    if (!currentVariant || Object.keys(currentVariant).length === 0) {
      return this.hqrConfig.defaultImage;
    }

    const resolver = new HqrImageResolver(this.hqrConfig.config);
    const resolvedImage = resolver.resolve(currentVariant);

    if (resolvedImage) {
      return resolvedImage;
    }

    return this.hqrConfig.defaultImage;
  }

  /**
   * Load HQR configuration from API (called once on init)
   */
  async loadHqrConfig(): Promise<void> {
    try {
      const config = await this.params.fetcher.getHqrConfig(this.params.params);
      runInAction(() => {
        this.hqrConfig = config;
      });
    } catch (error) {
      console.warn('Failed to load HQR config:', error);
      runInAction(() => {
        this.hqrConfig = null;
      });
    }
  }

  /**
   * Fetch HQR (High Quality Rendering) configuration from the API
   * @returns HQR configuration DTO with enabled flag, default image, and config entries
   * @deprecated Use loadHqrConfig() to load once on init and hqrImageUrl to get current image
   */
  async getHqrConfig(): Promise<IHqrConfiguratorDto> {
    return this.params.fetcher.getHqrConfig(this.params.params);
  }

  /**
   * Resolve HQR image URL for the current product variant
   * @param hqrConfig The HQR configuration from getHqrConfig()
   * @returns The resolved image URL or null if no match
   * @deprecated Use hqrImageUrl computed property instead
   */
  resolveHqrImage(hqrConfig: IHqrConfiguratorDto): string | null {
    if (!hqrConfig.enabled || !this.sdk.composite) {
      return null;
    }

    const resolver = new HqrImageResolver(hqrConfig.config);
    const currentVariant = this.sdk.api.getVariant();
    const resolvedImage = resolver.resolve(currentVariant);

    if (resolvedImage) {
      return resolvedImage;
    }

    return hqrConfig.defaultImage;
  }

  private async loadLocalizationConfig(): Promise<ILocalizationConfigDto> {
    if (!this.isCatalog) return DEFAULT_LOCALIZATION_CONFIG;
    try {
      const url = `${AppConfig.serverUrl}/configurator/localization-config?catalogId=${this.catalogId}&version=${
        this.draft ? 'draft' : 'active'
      }`;
      const response = await fetch(url);
      if (response.ok) return await response.json();
    } catch (error) {
      console.warn('Failed to load localization config; using defaults', error);
    }
    return DEFAULT_LOCALIZATION_CONFIG;
  }

  private subscribeToAutoCorrection() {
    // TODO: Add to SDK
    // this.engine.notifications.subscribe<LookupMutatorCorrectionEvent>(LookupMutator.CorrectionEvent, (event) => {
    //   const translator = new OptionsCorrectionsTranslator(this.localization);
    //   const messages = translator.translate(event.optionsCorrections);
    //   if (messages.length) {
    //     this.notificationService.info(messages);
    //   }
    // });
  }

  get skipRendering() {
    return SKIP_RENDERING || this.params.without3d;
  }

  async init() {
    if (!this.skipRendering) {
      try {
        // Inside the try on purpose: a chunk that fails to load (404 after a deploy, offline)
        // rejects here and lands in the same catch as any other 3D init failure, so it shows
        // up as `view3dError` (rendered as ViewportErrorFallback) instead of taking the app down.
        const v3d = await this.#viewer3d!;
        const pipeRegistry = new v3d.PipeRegistry();
        pipeRegistry.registerPipe('ralToRgb', ralToHex);
        const dynamicValueService = new v3d.DynamicValueService(pipeRegistry);
        const view3dConfiguration = new v3d.View3dConfiguration(this.view3dConfigurationConfig);
        const partsRegistry = new PartsRegistry<ProductPartViewNode>();
        this.context = new v3d.BuilderPlatformInfo(this.view3dConfigurationConfig, dynamicValueService, partsRegistry);
        // Inject the slot source once; the viewer renders slots itself on every
        // `showSceneV2()`. The SDK→render-spec mapping lives in clp-sdk, so the
        // viewer stays SDK-free and custom configurators reuse the same one line.
        if (this.sdk.composite) {
          this.context.setSlotProvider(() => buildSlotRenderSpecs(this.sdk.api));
        }
        this.builder = new v3d.PlatformBuilder(this.context);
        this.boundsDebugPlugin = new v3d.BoundsDebugPlugin();
        this.api = await new v3d.View3dFactory(this.canvas).createV2(
          view3dConfiguration,
          this.builder,
          new v3d.CompositePlugin([this.boundsDebugPlugin]),
        );

        const localizationConfig = await this.loadLocalizationConfig();
        const urlLocale = getUrlLocale();
        const effectiveLocale = resolveEffectiveLocale({
          urlLocale,
          catalogDefaultRegion: localizationConfig.defaultRegion,
        });
        const effectiveRegion = resolveEffectiveRegion({
          urlLocale,
          catalogDefaultRegion: localizationConfig.defaultRegion,
        });
        runInAction(() => {
          this.effectiveLocale = effectiveLocale;
          this.effectiveRegion = effectiveRegion;
        });
        this.api.setDimensionOptions({
          unitSystem: localizationConfig.unitSystem,
          region: effectiveRegion,
        });

        if (this.isCatalog) {
          this.pdfService = new PdfService(this.sdk, this.api, {
            catalogId: this.catalogId,
            draft: this.draft,
            selectedCurrency: this.selectedCurrency,
          });
        }

        // Reads the current productVariant, so every change made up to this point is
        // already included — only what lands during the awaits below still needs a replay.
        this.#pendingSceneSync = false;
        this.context.setProduct(toJS(this.productVariant));
        await this.api.showSceneV2();
        await this.updateBoundsDebug();
        this.#sceneReady = true;
        if (this.#pendingSceneSync) {
          this.#pendingSceneSync = false;
          this.syncScene();
        }
      } catch (error) {
        console.error('3D renderer initialization failed:', error);
        runInAction(() => {
          this.view3dError = error instanceof Error ? error : new Error(String(error));
        });
      }
    }

    await this.sdk.api.awaitLogicEngine();
    const { isValid, invalidRules } = this.sdk.api.validate({ ignoreAllRules: false });
    if (!isValid) {
      console.error('Invalid rules', invalidRules);
      let message = 'Invalid configuration';
      if (invalidRules.length > 0) {
        message += `. Check rules: ${invalidRules.join(', ')}`;
      }
      alert(message);
    }
  }

  async addToCart() {
    if (!this.sdk.composite) {
      throw new Error('Not supported');
    }
    const items = await this.sdk.api.getMetaIds();
    const msg = { type: 'add', items };
    const isIframe = window !== window.parent.window;
    if (isIframe && items.length > 0) {
      window.parent.postMessage(msg, '*');
    }
  }

  handleProductChanges() {
    this.productVariant = this.sdk.composite ? this.sdk.api.getVariant() : this.sdk.api.getVariant();
    this.updateTotalPrice();
    this.notifyHostPage();
    // The 3D sync may only run once init() has finished building the scene for the
    // first time: `this.api`/`this.context` are assigned in the middle of init()'s 3D
    // setup, several awaits before its own `setProduct`/`showSceneV2`, and the panel is
    // already interactive during that window (see BootstrapperContext's
    // earlyBootstrapper). Syncing there would run a scene build concurrent with init()'s.
    // Nothing is lost: init()'s own setProduct reads the current productVariant, and a
    // change landing during its `await showSceneV2()` is replayed via #pendingSceneSync.
    // If the 3D init failed, #sceneReady stays false and the sync is skipped for good —
    // there is no scene to sync to. The rest of this method runs in every case.
    if (!this.skipRendering) {
      if (this.#sceneReady) {
        this.syncScene();
      } else {
        this.#pendingSceneSync = true;
      }
    }

    this.lookupStorage.update();
    this.updateAllAttributes();
  }

  private syncScene() {
    // const sceneSpecification = this.sceneSpecificationBuilder.buildSpec(toJS(this.productVariant));
    // this.builder.update(toJS(this.productVariant));
    this.context.setProduct(toJS(this.productVariant));
    this.sdk.api.getMetaIds().then((metaIds) => console.log('meta ids: ', metaIds));
    this.api.showSceneV2();
    void this.updateBoundsDebug();
  }

  /**
   * Recompute the SDK-side bounding boxes for the current configuration and feed
   * them to the viewer's debug overlay. No-op unless the overlay is enabled
   * (`?debug`), so there's zero cost in normal use. The viewer stays SDK-free —
   * the SDK owns the bounds→tree mapping (`api.computeBoundsDebug`) and hands it
   * over as plain data.
   */
  private async updateBoundsDebug(): Promise<void> {
    const plugin = this.boundsDebugPlugin;
    if (!plugin?.enabled || !this.sdk.composite) {
      return;
    }
    try {
      plugin.setDebugInfo(await this.sdk.api.computeBoundsDebug());
    } catch (error) {
      console.warn('Bounds debug update failed:', error);
    }
  }

  private updateTotalPrice() {
    if (this.isCatalog) {
      this.sdk.api.getPrice().then((price) => runInAction(() => (this.totalPrice = price)));
    }
  }

  dispose() {
    this.api?.dispose();
  }

  private notifyHostPage() {
    if (this.sdk.composite) {
    }
  }

  private handleUpdateDependentAttributes(_payload: UpdateDependentAttributesEvent) {
    this.handleProductChanges();
  }

  private updateAllAttributes() {
    this.elements.forEach((element) => {
      element.update();
    });
  }

  async createLink(): Promise<string> {
    const shortLink = this.sdk.composite ? await this.sdk.api.createLink() : '';
    if (embeddingService.isEmbedded) {
      return await this.createEmbeddedLink(shortLink);
    }
    return addSearchParamToUrl(ConfiguratorParams.link, shortLink);
  }

  async downloadPdf(link: string, translations?: TranslationLayers) {
    await this.pdfService.downloadPdf(link, translations);
  }

  switchRuler(on?: boolean) {
    const newValue = on ?? !this.showRuler;

    if (newValue) {
      this.api.showRuler();
      this.showRuler = true;
    } else {
      this.api.hideRuler();
      this.showRuler = false;
    }
  }

  private async createEmbeddedLink(shortLink: string) {
    const location = await embeddingService.getLocation();
    return addSearchParamToHostUrl(ConfiguratorParams.link, shortLink, location);
  }
}
